/**
 * Client for the Rhythm API.
 *
 * Two things here are deliberate and worth not "simplifying" later:
 *
 * 1. **The session is a cookie, and no token is ever stored in JS.** Every
 *    request sets `credentials: "include"`. Nothing lands in localStorage,
 *    so a single XSS bug cannot walk off with a long-lived credential.
 *
 * 2. **The CSRF token is held in memory only.** Because the session cookie is
 *    sent cross-site in production (SameSite=None), the CSRF token is what
 *    actually stops a hostile page from acting as the signed-in user. Logging
 *    in renews the session and invalidates the old token, so the server hands
 *    back a fresh one and we swap it in.
 */

const API_URL = import.meta.env.VITE_API_URL ?? "";

/**
 * Where an uploaded image is fetched from. An id, never a URL the room chose —
 * the API decides what a given id resolves to, and whether this viewer may
 * see it at all.
 */
export const uploadUrl = (id: string) =>
  `${API_URL}/api/uploads/${encodeURIComponent(id)}`;

let csrfToken: string | null = null;

export class ApiError extends Error {
  status: number;
  code: string;
  fieldErrors?: Record<string, string[]>;

  constructor(status: number, code: string, message: string, fieldErrors?: Record<string, string[]>) {
    super(message);
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

async function ensureCsrf(): Promise<string> {
  if (csrfToken) return csrfToken;
  const res = await fetch(`${API_URL}/api/csrf`, { credentials: "include" });
  if (!res.ok) throw new ApiError(res.status, "csrf_failed", "Could not reach the server.");
  const body = await res.json();
  csrfToken = body.token;
  return body.token;
}

type Options = { method?: string; body?: unknown };

async function request<T>(path: string, { method = "GET", body }: Options = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["content-type"] = "application/json";

  // Only mutations need the token; GETs are not state-changing.
  if (method !== "GET") headers["x-csrf-token"] = await ensureCsrf();

  const res = await fetch(`${API_URL}/api${path}`, {
    method,
    headers,
    credentials: "include",
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  // A rotated or expired CSRF token is indistinguishable from a real refusal
  // at this layer, so retry exactly once with a fresh one rather than showing
  // the user a spurious permission error.
  if (res.status === 403 && method !== "GET") {
    csrfToken = null;
    const retryHeaders = { ...headers, "x-csrf-token": await ensureCsrf() };
    const retry = await fetch(`${API_URL}/api${path}`, {
      method,
      headers: retryHeaders,
      credentials: "include",
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    return handle<T>(retry);
  }

  return handle<T>(res);
}

async function handle<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;

  let payload: any = null;
  try {
    payload = await res.json();
  } catch {
    // Fall through to a generic error below.
  }

  if (!res.ok) {
    if (payload?.csrf_token) csrfToken = payload.csrf_token;
    throw new ApiError(
      res.status,
      payload?.error ?? "error",
      payload?.message ?? messageForStatus(res.status),
      payload?.errors,
    );
  }

  if (payload?.csrf_token) csrfToken = payload.csrf_token;
  return payload as T;
}

function messageForStatus(status: number): string {
  if (status === 404) return "Not found.";
  if (status === 401) return "You need to sign in.";
  if (status === 429) return "Too many attempts. Give it a minute.";
  return "Something went wrong.";
}

// ---------------------------------------------------------------- types

export type User = {
  id: number;
  /** Null for guests — theirs is a synthetic placeholder, not an address. */
  email: string | null;
  display_name: string;
  is_guest: boolean;
};
export type Participant = { id: number; display_name: string };
export type Role = "host" | "moderator" | "member";
export type AccessMode = "open" | "closed";

export type RoomSummary = {
  id: string;
  name: string;
  access_mode: AccessMode;
  theme: Theme;
};

export type Room = RoomSummary & {
  role: Role;
  is_owner: boolean;
};

export type Member = { user: Participant; role: Role; joined_at: string };
export type Knock = { id: number; user: Participant; message: string | null; requested_at: string };

export type JoinCode = {
  id: number;
  code?: string;
  label: string | null;
  expires_at: string | null;
  max_uses: number | null;
  use_count: number;
  created_at: string;
};

export type Theme = Record<string, string>;

export type MediaCredentials = {
  token: string;
  url: string;
  identity: string;
  expires_in: number;
};

// ---------------------------------------------------------------- endpoints

export const api = {
  me: () => request<{ user: User | null }>("/me"),

  register: (email: string, password: string, display_name: string) =>
    request<{ user: User }>("/register", {
      method: "POST",
      body: { email, password, display_name },
    }),

  /**
   * Signs in without an account. The session cookie is the only credential
   * that will ever exist for this user, so there is no way back in once it is
   * gone — the UI says as much before offering it.
   */
  guest: (display_name: string) =>
    request<{ user: User }>("/guest", { method: "POST", body: { display_name } }),

  login: (email: string, password: string) =>
    request<{ user: User }>("/login", { method: "POST", body: { email, password } }),

  logout: () => request<{ ok: boolean }>("/logout", { method: "DELETE" }),

  socketToken: () => request<{ token: string }>("/socket_token"),

  /**
   * Uploads an image for a room.
   *
   * Multipart, so it bypasses the JSON helper — but it still carries the CSRF
   * token and the session cookie, because it is a state-changing request like
   * any other.
   */
  uploadImage: async (roomId: string, file: File) => {
    // The image is the body, not a multipart form. There is one file and no
    // other fields, and the server identifies it from its own bytes rather
    // than from anything the form would have carried — so multipart added a
    // framing layer that could be malformed (and was) without adding
    // anything. Sent this way there is nothing between the bytes and the
    // endpoint to get wrong.
    const res = await fetch(`${API_URL}/api/rooms/${roomId}/uploads`, {
      method: "POST",
      credentials: "include",
      headers: {
        "x-csrf-token": await ensureCsrf(),
        "content-type": file.type || "application/octet-stream",
      },
      body: file,
    });
    return handle<{ upload: { id: string; content_type: string } }>(res);
  },

  updateProfile: (display_name: string) =>
    request<{ user: User }>("/me", { method: "PATCH", body: { display_name } }),

  listRooms: () => request<{ rooms: RoomSummary[] }>("/rooms"),

  createRoom: (name: string, access_mode: AccessMode) =>
    request<{ room: RoomSummary; join_code: JoinCode }>("/rooms", {
      method: "POST",
      body: { name, access_mode },
    }),

  getRoom: (id: string) => request<{ room: Room; members: Member[] }>(`/rooms/${id}`),

  updateRoom: (id: string, changes: { name?: string; access_mode?: AccessMode; theme?: Theme }) =>
    request<{ room: Room }>(`/rooms/${id}`, { method: "PATCH", body: changes }),

  /**
   * Redeems a code. Answers `joined` with the room, or `knocked` with only a
   * request id — a closed room tells you nothing until a host decides.
   */
  join: (code: string, message?: string) =>
    request<
      { status: "joined"; room: Room } | { status: "knocked"; knock_id: number }
    >("/rooms/join", { method: "POST", body: { code, message } }),

  mediaToken: (roomId: string) =>
    request<MediaCredentials>(`/rooms/${roomId}/token`, { method: "POST" }),

  listCodes: (roomId: string) => request<{ codes: JoinCode[] }>(`/rooms/${roomId}/codes`),

  createCode: (roomId: string, opts: { label?: string; max_uses?: number } = {}) =>
    request<{ code: JoinCode }>(`/rooms/${roomId}/codes`, { method: "POST", body: opts }),

  revokeCode: (codeId: number) =>
    request<{ status: string }>(`/codes/${codeId}`, { method: "DELETE" }),

  /**
   * The knocker's own view of their request. `room_id` appears only once a
   * host has approved it — that is the signal to walk in.
   */
  getKnock: (knockId: number) =>
    request<{ status: "pending" | "approved" | "denied"; room_id?: string }>(
      `/knocks/${knockId}`,
    ),

  listKnocks: (roomId: string) => request<{ knocks: Knock[] }>(`/rooms/${roomId}/knocks`),

  approveKnock: (knockId: number) =>
    request<{ status: string }>(`/knocks/${knockId}/approve`, { method: "POST" }),

  denyKnock: (knockId: number) =>
    request<{ status: string }>(`/knocks/${knockId}/deny`, { method: "POST" }),

  banMember: (roomId: string, userId: number) =>
    request<{ status: string }>(`/rooms/${roomId}/members/${userId}/ban`, { method: "POST" }),

  setRole: (roomId: string, userId: number, role: Role) =>
    request<{ status: string; role: Role }>(`/rooms/${roomId}/members/${userId}/role`, {
      method: "PATCH",
      body: { role },
    }),
};
